import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TopBar } from '@/components/TopBar';
import { Page } from '@/components/Page';
import GarmentAllocator from '@/components/GarmentAllocator';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import api, { errMsg } from '@/lib/api';
import { uid } from '@/lib/helpers';
import { useQuery } from '@tanstack/react-query';
import { Loader2, PackageCheck } from 'lucide-react';

export default function AddStock() {
  const navigate = useNavigate();
  const [garments, setGarments] = useState([]);
  const [supplierId, setSupplierId] = useState('');
  const [saving, setSaving] = useState(false);
  const { data: suppliers = [] } = useQuery({ queryKey: ['suppliers'], queryFn: async () => (await api.get('/suppliers')).data.data });

  const confirm = async () => {
    if (!garments.length) { toast.error('Add at least one garment.'); return; }
    if (garments.some((g) => !g.store_id)) { toast.error('Every garment must be allocated to a store.'); return; }
    setSaving(true);
    try {
      const r = await api.post('/stock/intake', { garments, supplier_id: supplierId || null, client_txn_id: uid() });
      toast.success(`${r.data.data.garments_created} garments added to stock`);
      navigate('/stock');
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  return (
    <Page>
      <TopBar title="Add Stock" back showStore={false} />
      <div className="px-4 pt-4 pb-32">
        <div className="rounded-2xl border border-border bg-card p-4">
          <Label className="text-xs">Supplier (optional)</Label>
          <Select value={supplierId} onValueChange={setSupplierId}>
            <SelectTrigger className="mt-1 h-11 rounded-xl" data-testid="addstock-supplier"><SelectValue placeholder="No supplier" /></SelectTrigger>
            <SelectContent>{suppliers.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}</SelectContent>
          </Select>
          <p className="mt-2 text-xs text-muted-foreground">For new stock from a supplier with a purchase value, use <button className="font-medium text-primary underline" onClick={() => navigate('/purchase')}>+ Purchase</button> instead.</p>
        </div>
        <div className="mt-4">
          <GarmentAllocator garments={garments} setGarments={setGarments} />
        </div>
      </div>
      <div className="fixed inset-x-0 bottom-16 z-40 border-t border-border bg-card/95 px-4 py-3 backdrop-blur" style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}>
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <span className="text-sm text-muted-foreground">{garments.length} garments ready</span>
          <Button data-testid="addstock-confirm" onClick={confirm} disabled={saving || !garments.length} className="h-12 rounded-xl px-6 font-semibold">
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <><PackageCheck className="mr-2 h-5 w-5" />Confirm Stock</>}
          </Button>
        </div>
      </div>
    </Page>
  );
}
